import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Table, Card, message, Spin, Modal, Button, Descriptions, Tabs, Typography } from 'antd';
import axios from 'axios';
import AuthContext from '../../context/auth/authContext';

const { Title } = Typography;

const SmartBuilderOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [orderLoading, setOrderLoading] = useState(false);
    const [token, setToken] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [jobDetails, setJobDetails] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const { user } = useContext(AuthContext);

    const convertToFeetAndInches = (totalFeet) => {
        const feet = Math.floor(totalFeet);
        const totalInches = (totalFeet - feet) * 12;
        const quarterInches = Math.round(totalInches * 4) / 4; // Round to nearest 1/4 inch
        return { feet, inches: quarterInches };
    };

    const materialColumns = [
        {
            title: 'FullSku',
            dataIndex: 'FullSku',
            key: 'FullSku',
        },
        {
            title: 'Material',
            dataIndex: 'Material',
            key: 'Material',
        },
        {
            title: 'Quantity',
            dataIndex: 'Qty',
            key: 'Qty',
        },
        {
            title: 'Feet',
            key: 'feet',
            render: (_, record) => {
                const { feet } = convertToFeetAndInches(record.Length);
                return feet;
            },
        },
        {
            title: 'Inches',
            key: 'inches',
            render: (_, record) => {
                const { inches } = convertToFeetAndInches(record.Length);
                return inches;
            },
        },
        {
            title: 'SellingUnit',
            dataIndex: 'SellingUnit',
            key: 'SellingUnit',
        }
    ];

    const getToken = async () => {
        try {
            const response = await axios.post('/api/users/smartbuilder/token', {
                username: user.smartBuilderUsername,
                password: user.smartBuilderPassword
            });

            setToken(response.data.access_token);
            return response.data.access_token;
        } catch (err) {
            console.error('Token error:', err);
            message.error('Failed to authenticate with SmartBuilder');
            return null;
        }
    };

    const fetchProjects = async (accessToken) => {
        try {
            const response = await axios.get('/api/users/smartbuilder/projects', {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            console.log('API Response:', response.data);

            // Transform the response data to match the table structure
            const transformedData = response.data.map((project, index) => ({
                key: index,
                jobId: project.JobId,
                name: project.Name,
                status: project.Status,
                userName: project.UserName,
                distributorName: project.DistributorName
            }));

            setOrders(transformedData);
        } catch (err) {
            console.error('Projects error:', err);
            message.error('Failed to fetch projects');
        }
    };

    const fetchData = useCallback(async () => {
        if (!user?.smartBuilderUsername || !user?.smartBuilderPassword) {
            message.error('Please configure your SmartBuilder credentials in Settings');
            return;
        }

        setLoading(true);
        try {
            const accessToken = await getToken();
            if (accessToken) {
                await fetchProjects(accessToken);
            }
        } catch (err) {
            console.error('Fetch error:', err);
            message.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const fetchJobDetails = async (jobId) => {
        try {
            setDetailLoading(true);
            const response = await axios.get(`/api/users/smartbuilder/job/${jobId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            console.log('Job Details Response:', response.data);

            if (!response.data || !Array.isArray(response.data.MaterialSections)) {
                console.error('Invalid data structure:', response.data);
                message.error('Invalid data received from server');
                return;
            }

            setJobDetails(response.data);
        } catch (err) {
            console.error('Job details error:', err);
            message.error('Failed to fetch job details');
        } finally {
            setDetailLoading(false);
        }
    };

    const handleViewOrder = async (order) => {
        setSelectedOrder(order);
        setModalVisible(true);
        await fetchJobDetails(order.jobId);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setJobDetails(null);
    };

    const handleOrderMetal = async () => {
        if (!selectedOrder || !jobDetails) return;

        try {
            console.log('Starting order metal process');
            setOrderLoading(true);

            // Filter items to only include Sheathing and Trim categories
            const smartBuilderItems = jobDetails.MaterialSections
                .filter(section => section.Name === 'Sheathing' || section.Name === 'Trim' || section.Name === 'Accessories')
                .flatMap(section => section.Materials.map(item => {
                    // Convert the length to total feet for the API
                    const { feet, inches } = convertToFeetAndInches(item.Length);
                    const totalFeet = feet + (inches / 12);

                    return {
                        FullSku: item.FullSku,
                        Material: item.Material,
                        Qty: item.Qty,
                        Length: totalFeet, // Send total feet to the API
                        DisplayLength: `${feet}' ${inches}"`, // Keep formatted length for display
                        decLinearAmount1: feet,
                        decLinearAmount2: inches,
                        SellingUnit: item.SellingUnit,
                        Category: section.Name
                    };
                }));

            // console.log('Filtered SmartBuilder items:', smartBuilderItems);

            if (smartBuilderItems.length === 0) {
                message.warning('No valid items found to order. Please ensure there are Sheathing or Trim items in the order.');
                return;
            }

            // Use the project name as the purchase order number
            const purchaseOrderNumber = selectedOrder.name;
            console.log('Using purchase order number:', purchaseOrderNumber);

            const orderData = {
                metalOrders: {
                    purchaseOrderNumber,
                    metalOrder: [] // Empty array since we're only ordering SmartBuilder items
                },
                smartBuilderItems
            };

            // console.log('Sending order data:', orderData);

            const response = await axios.post('/api/metalorder', orderData);
            // console.log('Order response:', response.data);

            message.success('Metal order placed successfully');
            setModalVisible(false);
            setJobDetails(null);
            setSelectedOrder(null);
            // Refresh the orders list
            fetchData();
        } catch (err) {
            console.error('Order error:', err);
            console.error('Error response:', err.response?.data);
            message.error(err.response?.data?.message || 'Failed to place metal order');
        } finally {
            setOrderLoading(false);
        }
    };

    const columns = [
        {
            title: 'Job ID',
            dataIndex: 'jobId',
            key: 'jobId',
        },
        {
            title: 'Project Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
        },
        // {
        //     title: 'User',
        //     dataIndex: 'userName',
        //     key: 'userName',
        // },
        // {
        //     title: 'Distributor',
        //     dataIndex: 'distributorName',
        //     key: 'distributorName',
        // },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button type="link" onClick={() => handleViewOrder(record)}>
                    View Details
                </Button>
            ),
        },
    ];

    const renderMaterialSection = (section) => {
        if (!section.Materials || section.Materials.length === 0) return null;

        return (
            <div key={section.Name} style={{ marginBottom: '30px', borderBottom: '1px solid #f0f0f0', paddingBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <Title level={4} style={{ margin: 0 }}>{section.Name}</Title>
                    {/* <div style={{ textAlign: 'right' }}>
                        <div><strong>Subtotal: ${parseFloat(section.Subtotal).toFixed(2)}</strong></div>
                        {section.Weight > 0 && (
                            <div>Weight: {parseFloat(section.Weight).toFixed(2)} lbs</div>
                        )}
                    </div> */}
                </div>
                <Table
                    columns={materialColumns}
                    dataSource={section.Materials}
                    rowKey={(record) => record.VendorSku}
                    pagination={false}
                    scroll={{ x: true }}
                    size="small"
                />
            </div>
        );
    };

    return (
        <div style={{ padding: '24px' }}>
            <Card title="SmartBuild Orders">
                <Spin spinning={loading}>
                    <Table
                        columns={columns}
                        dataSource={orders}
                        rowKey="key"
                        pagination={{
                            defaultPageSize: 10,
                            pageSize: undefined,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            // showQuickJumper: true
                        }}
                    />
                </Spin>
            </Card>

            <Modal
                title="Order Details"
                open={modalVisible}
                onCancel={handleModalClose}
                footer={[
                    <Button key="order" type="primary" onClick={handleOrderMetal} loading={orderLoading}>
                        Order Metal
                    </Button>,
                    <Button key="close" onClick={handleModalClose}>
                        Close
                    </Button>
                ]}
                width={1200}
            >
                <Spin spinning={detailLoading}>
                    {selectedOrder && (
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label="Job ID">{selectedOrder.jobId}</Descriptions.Item>
                            <Descriptions.Item label="Project Name">{selectedOrder.name}</Descriptions.Item>
                            <Descriptions.Item label="Status">{selectedOrder.status}</Descriptions.Item>
                            {/* <Descriptions.Item label="User">{selectedOrder.userName}</Descriptions.Item> */}
                            {/* <Descriptions.Item label="Distributor">{selectedOrder.distributorName}</Descriptions.Item> */}
                        </Descriptions>
                    )}

                    {jobDetails && (
                        <div style={{ marginTop: '20px' }}>
                            <Tabs defaultActiveKey="materials">
                                <Tabs.TabPane tab="Materials" key="materials">
                                    {jobDetails.MaterialSections && jobDetails.MaterialSections.length > 0 ? (
                                        <div>
                                            {jobDetails.MaterialSections.map(section => renderMaterialSection(section))}
                                            <div style={{
                                                marginTop: '20px',
                                                padding: '20px',
                                                backgroundColor: '#f5f5f5',
                                                borderRadius: '4px'
                                            }}>
                                                {/* <Title level={4}>Order Summary</Title>
                                                <Descriptions bordered column={2}>
                                                    <Descriptions.Item label="Taxable Subtotal">
                                                        ${parseFloat(jobDetails.TaxableSubtotal).toFixed(2)}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Non-Taxable Subtotal">
                                                        ${parseFloat(jobDetails.NonTaxableSubtotal).toFixed(2)}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Sales Tax Rate">
                                                        {jobDetails.SalesTaxRate}%
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Sales Tax">
                                                        ${parseFloat(jobDetails.SalesTax).toFixed(2)}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Total Price">
                                                        <strong>${parseFloat(jobDetails.TotalPrice).toFixed(2)}</strong>
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Total Weight">
                                                        {parseFloat(jobDetails.Weight).toFixed(2)} lbs
                                                    </Descriptions.Item>
                                                </Descriptions> */}
                                            </div>
                                        </div>
                                    ) : (
                                        <div>No materials found</div>
                                    )}
                                </Tabs.TabPane>
                                <Tabs.TabPane tab="Project Info" key="info">
                                    {jobDetails.Answers && jobDetails.Answers.length > 0 ? (
                                        <Descriptions bordered column={2}>
                                            {jobDetails.Answers.map((answer) => (
                                                <Descriptions.Item key={answer.Id} label={answer.Id}>
                                                    {answer.Value}
                                                </Descriptions.Item>
                                            ))}
                                        </Descriptions>
                                    ) : (
                                        <div>No project information available</div>
                                    )}
                                </Tabs.TabPane>
                            </Tabs>
                        </div>
                    )}
                </Spin>
            </Modal>
        </div>
    );
};

export default SmartBuilderOrders; 